import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { MsalService, MsalBroadcastService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { InteractionType } from '@azure/msal-browser';
import { AuthenticationService } from './services/authentication.service';
import { UserService, CurrentUserInfoResponse } from './services/user.service';
import { environment } from '../environments/environment';
import { bootstrapChunkErrorRecovery } from './services/chunk-error-handler.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'mat-track-app';
  private readonly _destroying$ = new Subject<void>();
  
  // User info for display
  currentUser: any = null;
  userInitials: string = '';
  userName: string = '';
  userEmail: string = '';
  userRole: string = '';
  userRoleInitials: string = '';
  isAuthenticated: boolean = false;
  userPhotoUrl: string | null = null;
  
  // True once the backend /getUserInfo response has arrived (success or failure)
  userInfoLoaded: boolean = false;

  // True when the current route is /logged-out (no shell needed)
  isLoggedOutPage: boolean = window.location.pathname === '/logged-out';

  // Reports config from environment
  reportsConfig = environment.reports;

  // User menu dropdown state
  showUserMenu: boolean = false;
  showReportsMenu: boolean = false;
  private onDocumentClick = (_: any) => { this.showUserMenu = false; this.showReportsMenu = false; };

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private authenticationService: AuthenticationService,
    private userService: UserService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Intercept NavigationErrors caused by stale chunk hashes after deployment
    bootstrapChunkErrorRecovery(this.router);

    this.msalBroadcastService.inProgress$
      .pipe(takeUntil(this._destroying$))
      .subscribe(() => {
        this.setLoginDisplay();
      });
    
    this.authenticationService.init();

    // Subscribe to backend user info for role display and nav gating.
    // userInfoLoaded is set to true once the first non-null response arrives,
    // preventing the nav from rendering until the role is known.
    this.userService.currentUserInfo$
      .pipe(takeUntil(this._destroying$))
      .subscribe((info: CurrentUserInfoResponse | null) => {
        const user = info?.user;
        if (user) {
          this.userRole = this.mapUserRole(user.user_role_cd);
          this.userRoleInitials = this.mapUserRoleInitials(this.userRole);
          this.userInfoLoaded = true;
        } else if (info !== null) {
          // API responded but no user found (e.g. not in mat_user table)
          this.userRole = '';
          this.userRoleInitials = '';
          this.userInfoLoaded = true;
        }
        // info === null means the initial BehaviorSubject value — keep waiting
      });
    
    // Close user menu on outside click
    document.addEventListener('click', this.onDocumentClick);
  }

  setLoginDisplay() {
    this.isAuthenticated = this.authService.instance.getAllAccounts().length > 0;
    
    if (this.isAuthenticated) {
      this.currentUser = this.authService.instance.getAllAccounts()[0];
      this.authenticationService.updateCurrentUser(this.currentUser);
      
      // Extract user info
      // Capitalize first letter of each part of the name
      const rawName = this.currentUser.name || 'User';
      this.userName = rawName.split(' ').map((part: string) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
      this.userEmail = this.currentUser.username || this.currentUser.idTokenClaims?.email || this.currentUser.idTokenClaims?.preferred_username || '';
      
      const names = this.userName.split(' ');
      this.userInitials = names.length >= 2 
        ? names[0][0] + names[names.length - 1][0]
        : this.userName.substring(0, 2);
      this.userInitials = this.userInitials.toUpperCase();
      
      // Set role from token claims if available
      // Display role primarily from backend user info (handled via UserService subscription)
      
      // Fetch user photo from Microsoft Graph
      this.fetchUserPhoto();
    }
  }

  private mapUserRole(code: string | undefined | null): string {
    if (!code) {
      return '';
    }
    const normalized = code.trim().toLowerCase();
    if (normalized === 'mat coordinator') {
      return 'Mat Coordinator';
    }
    if (normalized === 'planner') {
      return 'Planner';
    }
    if (normalized === 'pm') {
      return 'Project Manager';
    }
    return code;
  }

  private mapUserRoleInitials(displayRole: string | undefined | null): string {
    if (!displayRole) {
      return '';
    }
    const normalized = displayRole.trim().toLowerCase();
    // Special-case Project Manager so initials stay "PM"
    if (normalized === 'project manager') {
      return 'PM';
    }
    const parts = displayRole.trim().split(/\s+/).filter(p => p.length > 0);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    if (displayRole.length >= 2) {
      return displayRole.substring(0, 2).toUpperCase();
    }
    return displayRole.toUpperCase();
  }

  get isMatCoordinator(): boolean {
    if (this.userRole !== 'Mat Coordinator') {
      return false;
    }
    // Also verify the Azure AD token role
    const account = this.authService.instance.getActiveAccount();
    if (account?.idTokenClaims && (account.idTokenClaims as any).roles) {
      const roles = (account.idTokenClaims as any).roles as string[];
      return roles.includes('MatTrack.Admin.Users');
    }
    return false;
  }

  fetchUserPhoto() {
    // Get access token for Microsoft Graph
    this.authService.acquireTokenSilent({
      scopes: ['User.Read'],
      account: this.currentUser
    }).subscribe({
      next: (result) => {
        // Fetch photo from Microsoft Graph API
        fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
          headers: {
            'Authorization': `Bearer ${result.accessToken}`
          }
        })
        .then(response => {
          if (response.ok) {
            return response.blob();
          }
          throw new Error('No photo available');
        })
        .then(blob => {
          this.userPhotoUrl = URL.createObjectURL(blob);
        })
        .catch(error => {
          console.log('User photo not available, using initials');
          this.userPhotoUrl = null;
        });
      },
      error: (error) => {
        console.log('User photo not available:', error.message || 'Token acquisition failed');
        this.userPhotoUrl = null;
      }
    });
  }

  toggleReportsMenu(event?: Event) {
    if (event) { event.stopPropagation(); }
    this.showReportsMenu = !this.showReportsMenu;
    this.showUserMenu = false;
  }

  openReport(url: string) {
    window.open(url, '_blank');
    this.showReportsMenu = false;
  }

  toggleUserMenu(event?: Event) {
    if (event) { event.stopPropagation(); }
    this.showUserMenu = !this.showUserMenu;
    this.showReportsMenu = false;
  }

  logout() {
    this.showUserMenu = false;
    const account = this.authService.instance.getActiveAccount()
                 ?? this.authService.instance.getAllAccounts()[0];
    // username is the UPN (email) — always present, most reliable hint
    const hint = account?.username ?? (account?.idTokenClaims as any)?.login_hint;

    // Clear MSAL cache so tokens are gone immediately
    this.authService.instance.clearCache();

    // Navigate directly to Azure AD logout URL.
    // logout_hint skips the "Pick an account" screen.
    // post_logout_redirect_uri sends user to /logged-out (unguarded route).
    const base   = `https://login.microsoftonline.com/${environment.azureTenantId}/oauth2/v2.0/logout`;
    const params = new URLSearchParams({
      post_logout_redirect_uri: `${window.location.origin}/logged-out`
    });
    if (hint) params.set('logout_hint', hint);

    window.location.href = `${base}?${params.toString()}`;
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.onDocumentClick);
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}